import type {
  BlogCluster,
  BlogPost,
  BlogPostSummary,
} from "@/shared/blog/blogTypes";
import {
  DEFAULT_ORIGIN,
  buildArticleJsonLd,
  buildBlogListJsonLd,
  buildBreadcrumbJsonLd,
  buildClusterBreadcrumbJsonLd,
  buildClusterJsonLd,
  buildClusterMeta,
  buildPostMeta,
} from "@/shared/blog/seo";
import { Helmet } from "react-helmet-async";

const OG_FALLBACK = `${DEFAULT_ORIGIN}/og-image.png`;

export const BlogPostSeo = ({ post }: { post: BlogPost }) => {
  const meta = buildPostMeta(post);
  const ogImage = meta.ogImage;
  const article = buildArticleJsonLd(post);
  const breadcrumb = buildBreadcrumbJsonLd(post);

  // titleTemplate="%s": o título do blog já sai completo do seo.ts — o mesmo
  // que o SSR entrega. Com o "%s | Fidexa" global o cliente duplicava o
  // sufixo ("… | Fidexa | Fidexa" no hub).
  return (
    <Helmet titleTemplate="%s">
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      {post.seo.noindex && <meta name="robots" content="noindex, follow" />}
      {post.tags.length > 0 && <meta name="keywords" content={post.tags.join(", ")} />}

      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Fidexa" />
      <meta property="og:title" content={meta.ogTitle} />
      <meta property="og:description" content={meta.ogDescription} />
      <meta property="og:url" content={meta.ogUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="pt_BR" />
      {post.publishedAt && (
        <meta property="article:published_time" content={post.publishedAt} />
      )}
      <meta property="article:modified_time" content={post.updatedAt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.ogTitle} />
      <meta name="twitter:description" content={meta.ogDescription} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">{JSON.stringify(article)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
  );
};

const LIST_TITLE = "Blog da Fidexa — Crediário, cobrança e inadimplência no varejo";
const LIST_DESCRIPTION =
  "Conteúdos práticos sobre gestão de crediário, recuperação de crédito, cobrança via WhatsApp e redução de inadimplência para o varejo brasileiro.";
const LIST_URL = `${DEFAULT_ORIGIN}/blog`;

export const BlogListSeo = ({ canonical = LIST_URL }: { canonical?: string }) => {
  const blogJsonLd = buildBlogListJsonLd();

  // titleTemplate="%s": o título do blog já sai completo do seo.ts — o mesmo
  // que o SSR entrega. Com o "%s | Fidexa" global o cliente duplicava o
  // sufixo ("… | Fidexa | Fidexa" no hub).
  return (
    <Helmet titleTemplate="%s">
      <title>{LIST_TITLE}</title>
      <meta name="description" content={LIST_DESCRIPTION} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fidexa" />
      <meta property="og:title" content={LIST_TITLE} />
      <meta property="og:description" content={LIST_DESCRIPTION} />
      <meta property="og:url" content={LIST_URL} />
      <meta property="og:image" content={OG_FALLBACK} />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={LIST_TITLE} />
      <meta name="twitter:description" content={LIST_DESCRIPTION} />
      <meta name="twitter:image" content={OG_FALLBACK} />

      <script type="application/ld+json">{JSON.stringify(blogJsonLd)}</script>
    </Helmet>
  );
};

/**
 * Head do hub de cluster no cliente.
 *
 * Reaproveita exatamente os mesmos builders do SSR (`buildClusterMeta`,
 * `buildClusterJsonLd`, `buildClusterBreadcrumbJsonLd`) porque head servido e
 * head hidratado divergirem é como nasce canonical duplicado: o robô lê um, o
 * usuário navegando dentro do SPA vê outro.
 */
export const BlogClusterSeo = ({
  cluster,
  posts,
}: {
  cluster: BlogCluster;
  posts: BlogPostSummary[];
}) => {
  const meta = buildClusterMeta(cluster);

  // titleTemplate="%s": o título do blog já sai completo do seo.ts — o mesmo
  // que o SSR entrega. Com o "%s | Fidexa" global o cliente duplicava o
  // sufixo ("… | Fidexa | Fidexa" no hub).
  return (
    <Helmet titleTemplate="%s">
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      <meta name="robots" content="index, follow" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fidexa" />
      <meta property="og:title" content={meta.ogTitle} />
      <meta property="og:description" content={meta.ogDescription} />
      <meta property="og:url" content={meta.ogUrl} />
      <meta property="og:image" content={meta.ogImage} />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.ogTitle} />
      <meta name="twitter:description" content={meta.ogDescription} />
      <meta name="twitter:image" content={meta.ogImage} />

      <script type="application/ld+json">
        {JSON.stringify(buildClusterJsonLd(cluster, posts))}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(buildClusterBreadcrumbJsonLd(cluster))}
      </script>
    </Helmet>
  );
};
